import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { inviteParticipant, getAvailableEnablers } from '@/lib/api/initiatives';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { EnablerInfo } from '@/types/initiative';

interface InviteParticipantModalProps {
  initiativeId: string;
  locale: Locale;
  onSuccess: () => void;
  onClose: () => void;
}

type InviteMode = 'existing' | 'email';

export function InviteParticipantModal({
  initiativeId,
  locale,
  onSuccess,
  onClose,
}: InviteParticipantModalProps) {
  const isRTL = locale === 'ar';
  const [mode, setMode] = useState<InviteMode>('existing');
  const [enablers, setEnablers] = useState<EnablerInfo[]>([]);
  const [enablersLoading, setEnablersLoading] = useState(false);
  const [selectedEnablerId, setSelectedEnablerId] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEnablersLoading(true);
    getAvailableEnablers()
      .then((response) => {
        if (!cancelled && response.success && response.data) {
          setEnablers(response.data);
        }
      })
      .finally(() => {
        if (!cancelled) setEnablersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isValid = mode === 'existing' ? Boolean(selectedEnablerId) : Boolean(email.trim());

  const handleSubmit = async () => {
    if (!isValid) {
      setError(t(locale, 'requiredField'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await inviteParticipant(
        initiativeId,
        mode === 'existing'
          ? { initiative_id: initiativeId, enabler_id: selectedEnablerId }
          : { initiative_id: initiativeId, email: email.trim() },
      );
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError(response.message || t(locale, 'error'));
      }
    } catch {
      setError(t(locale, 'error'));
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
    >
      <div className="bg-card w-full max-w-[480px] rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 text-center">
          <h2 id="invite-modal-title" className="text-2xl font-bold text-foreground mb-2">
            {t(locale, 'shareImpact')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {t(locale, 'shareImpactTagline')}
          </p>
        </div>
        <div className="border-t border-gray-100 dark:border-gray-700 mx-8" />

        <div className="p-8 space-y-6">
          {success ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <span className="material-icons-outlined text-green-500 text-3xl">check_circle</span>
              </div>
              <p className="font-medium text-foreground">
                {t(locale, 'inviteSentSuccess')}
              </p>
            </div>
          ) : (
            <>
              {/* Mode toggle */}
              <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button
                  type="button"
                  onClick={() => setMode('existing')}
                  className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-colors ${
                    mode === 'existing'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {t(locale, 'existingEnabler')}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('email')}
                  className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-colors ${
                    mode === 'email'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {t(locale, 'inviteByEmail')}
                </button>
              </div>

              {mode === 'existing' ? (
                <div className="space-y-2">
                  <label htmlFor="invite-enabler" className={`block text-gray-700 dark:text-gray-300 font-medium text-sm ${isRTL ? 'text-right' : ''}`}>
                    {t(locale, 'whoToInvite')}
                  </label>
                  <select
                    id="invite-enabler"
                    value={selectedEnablerId}
                    onChange={(e) => setSelectedEnablerId(e.target.value)}
                    disabled={enablersLoading}
                    className="w-full px-4 py-3 bg-card border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-gray-900 disabled:opacity-60"
                  >
                    <option value="" disabled>
                      {enablersLoading
                        ? t(locale, 'loading')
                        : t(locale, 'selectEnabler')}
                    </option>
                    {enablers.map((enabler) => (
                      <option key={enabler.id} value={enabler.id}>
                        {isRTL ? enabler.name_ar : enabler.name_en}
                      </option>
                    ))}
                  </select>
                  {!enablersLoading && enablers.length === 0 && (
                    <p className="text-xs text-gray-400">{t(locale, 'noEnablersAvailable')}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <label htmlFor="invite-identifier" className={`block text-gray-700 dark:text-gray-300 font-medium text-sm ${isRTL ? 'text-right' : ''}`}>
                    {t(locale, 'whoToInvite')}
                  </label>
                  <input
                    id="invite-identifier"
                    type="email"
                    dir="auto"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    placeholder={t(locale, 'inviteIdentifierPlaceholder')}
                    className="w-full px-4 py-3 bg-card border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-400 text-gray-900"
                    aria-describedby={error ? 'invite-error' : undefined}
                    autoFocus
                  />
                </div>
              )}

              {error && (
                <p id="invite-error" className="text-xs text-red-500">{error}</p>
              )}

              <div className={`flex gap-3 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !isValid}
                  aria-label={t(locale, 'requestJoin')}
                  className={`flex-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  {submitting ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{t(locale, 'requestJoin')}</span>
                      <span className={`material-icons-outlined text-xl ${isRTL ? '' : 'rotate-180'}`}>send</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold py-3.5 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {t(locale, 'cancelAction')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
